import React, { Suspense } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Modal Loading Fallback - 모달 로딩 시 표시될 스켈레톤 UI
 */
const ModalLoadingFallback: React.FC = () => (
  <DialogContent className="max-w-md">
    <DialogHeader className="sr-only">
      <DialogTitle>Loading</DialogTitle>
      <DialogDescription>Content is loading, please wait...</DialogDescription>
    </DialogHeader>
    <div className="space-y-4 p-2">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      
      {/* Content skeleton */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      
      {/* Action buttons skeleton */}
      <div className="flex justify-end space-x-2 pt-4">
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-20" />
      </div>
    </div>
  </DialogContent>
);

/**
 * Modal Wrapper Props Interface
 */
interface ModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  fallbackContent?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * ModalWrapper - 모달 lazy loading을 위한 래퍼 컴포넌트
 * 
 * 특징:
 * - 모달이 열릴 때만 실제 컴포넌트 로드
 * - 로딩 중에는 스켈레톤 UI 표시
 * - 닫힌 상태에서는 아무것도 렌더링하지 않음 (메모리 효율성)
 */
export const ModalWrapper: React.FC<ModalWrapperProps> = ({
  isOpen,
  onClose,
  fallbackContent,
  children
}) => {
  // 모달이 닫혀있으면 아무것도 렌더링하지 않음
  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <Suspense fallback={fallbackContent || <ModalLoadingFallback />}>
        {children}
      </Suspense>
    </Dialog>
  );
};

/**
 * LazyModal HOC - 모달 컴포넌트를 lazy loading으로 변환하는 고차 컴포넌트
 */
// eslint-disable-next-line react-refresh/only-export-components
export const createLazyModal = <T extends object>(
  modalImport: () => Promise<{ default: React.ComponentType<T> }>,
  customFallback?: React.ReactNode
) => {
  const LazyModalComponent = React.lazy(modalImport);
  
  return (props: T & { isOpen?: boolean; onClose?: () => void }) => {
    const { isOpen = false, onClose = () => {}, ...modalProps } = props;
    
    return (
      <ModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        fallbackContent={customFallback}
      >
        <LazyModalComponent 
          {...(modalProps as T)} 
          isOpen={isOpen}
          onClose={onClose}
        />
      </ModalWrapper>
    );
  };
};

/**
 * Optimized modal loading utility
 * 모달이 자주 사용되는 경우를 위한 사전 로딩 유틸리티
 */
// eslint-disable-next-line react-refresh/only-export-components
export const preloadModal = (modalImport: () => Promise<{ default: React.ComponentType<unknown> }>) => {
  // 브라우저가 유휴 상태일 때 모달을 사전 로딩
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      modalImport();
    });
  } else {
    // requestIdleCallback을 지원하지 않는 브라우저를 위한 대체
    setTimeout(() => {
      modalImport();
    }, 100);
  }
};

export default ModalWrapper;